import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar,
  CheckSquare,
  Gavel,
  FileText,
  Video,
  Brain,
  ClipboardList,
  RefreshCw,
  ChevronRight,
  ArrowRight,
  Clock,
  TrendingUp,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectTimeline = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [filterType, setFilterType] = useState('');
  const [projectName, setProjectName] = useState('');

  const token = sessionStorage.getItem('auth_token');

  const fetchTimeline = useCallback(async () => {
    try {
      let url = `${API_URL}/api/meeting-intelligence/projects/${projectId}/activity-timeline?token=${token}&days=${days}`;
      if (filterType) {
        url += `&event_types=${filterType}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setTimeline(data.timeline || []);
        setProjectName(data.project_name || '');
      }
    } catch (error) {
      console.error('Error fetching timeline:', error);
      toast.error('Failed to load timeline');
    }
  }, [projectId, token, days, filterType]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/meeting-intelligence/projects/${projectId}/timeline/summary?token=${token}&days=${days}`
      );
      const data = await response.json();
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [projectId, token, days]);

  // Progressive — timeline + summary paint independently.
  useEffect(() => {
    setLoading(true);
    const tasks = [fetchTimeline(), fetchSummary()].map((p) =>
      Promise.resolve(p).catch((e) => { console.error('[ProjectTimeline]', e); return null; }));
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  }, [fetchTimeline, fetchSummary]);

  const getEventIcon = (type) => {
    switch (type) {
      case 'meeting': return <Calendar className="h-5 w-5 text-blue-500" />;
      case 'task_created': return <CheckSquare className="h-5 w-5 text-green-500" />;
      case 'task_completed': return <CheckSquare className="h-5 w-5 text-emerald-600" />;
      case 'decision': return <Gavel className="h-5 w-5 text-purple-500" />;
      case 'mom_generated': return <ClipboardList className="h-5 w-5 text-orange-500" />;
      case 'recording_ready': return <Video className="h-5 w-5 text-red-500" />;
      case 'transcript_completed': return <FileText className="h-5 w-5 text-cyan-500" />;
      case 'analysis_completed': return <Brain className="h-5 w-5 text-pink-500" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getEventBadgeColor = (type) => {
    switch (type) {
      case 'meeting': return 'bg-blue-100 text-blue-800';
      case 'task_created': return 'bg-green-100 text-green-800';
      case 'task_completed': return 'bg-emerald-100 text-emerald-800';
      case 'decision': return 'bg-purple-100 text-purple-800';
      case 'mom_generated': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventType = (type) => {
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="timeline-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-timeline">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Project Timeline</h2>
          <p className="text-gray-500 text-sm">{projectName} - Activity over last {days} days</p>
        </div>
        <div className="flex gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 px-3 border rounded-md text-sm"
            data-testid="days-filter"
          >
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last year</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="h-9 px-3 border rounded-md text-sm"
            data-testid="type-filter"
          >
            <option value="">All Events</option>
            <option value="meeting">Meetings Only</option>
            <option value="task">Tasks Only</option>
            <option value="decision">Decisions Only</option>
            <option value="mom">MoM Only</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchTimeline}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-4 gap-4" data-testid="timeline-summary">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-3xl font-bold text-blue-600">{summary.meetings?.total || 0}</p>
                  <p className="text-sm text-gray-500">Meetings</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-200" />
              </div>
              <div className="mt-2 flex items-center text-xs text-green-600">
                <TrendingUp className="h-3 w-3 mr-1" />
                {summary.meetings?.completion_rate || 0}% completed
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-3xl font-bold text-green-600">{summary.tasks?.total || 0}</p>
                  <p className="text-sm text-gray-500">Tasks</p>
                </div>
                <CheckSquare className="h-8 w-8 text-green-200" />
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                <span className="text-green-600">{summary.tasks?.completed || 0} done</span>
                <span className="text-orange-600">{summary.tasks?.pending || 0} pending</span>
                {summary.tasks?.overdue > 0 && (
                  <span className="text-red-600">{summary.tasks?.overdue} overdue</span>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-3xl font-bold text-purple-600">{summary.decisions?.total || 0}</p>
                  <p className="text-sm text-gray-500">Decisions</p>
                </div>
                <Gavel className="h-8 w-8 text-purple-200" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Key decisions made
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-3xl font-bold text-orange-600">{summary.minutes_generated || 0}</p>
                  <p className="text-sm text-gray-500">Minutes Generated</p>
                </div>
                <ClipboardList className="h-8 w-8 text-orange-200" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                AI-generated MoM
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline */}
      <Card data-testid="timeline-events">
        <CardHeader>
          <CardTitle>Activity Timeline</CardTitle>
          <CardDescription>{timeline.length} events in the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {timeline.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No events found for the selected period</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
              
              <div className="space-y-6">
                {timeline.map((event, index) => (
                  <div
                    key={event.id}
                    className="relative flex gap-4 group"
                    data-testid={`timeline-event-${index}`}
                  >
                    {/* Icon */}
                    <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-white border-2 border-gray-200 rounded-full group-hover:border-blue-300 transition-colors">
                      {getEventIcon(event.type)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getEventBadgeColor(event.type)}>
                          {formatEventType(event.type)}
                        </Badge>
                        {event.status && (
                          <Badge variant="outline">{event.status}</Badge>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                          {formatDate(event.timestamp)}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-900">{event.title}</h4>
                      {event.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      {/* Metadata */}
                      {event.metadata && (
                        <div className="flex gap-3 mt-2 text-xs text-gray-400">
                          {event.metadata.assigned_to && (
                            <span>Assigned to: {event.metadata.assigned_to}</span>
                          )}
                          {event.metadata.priority && (
                            <span>Priority: {event.metadata.priority}</span>
                          )}
                          {event.metadata.impact && (
                            <span>Impact: {event.metadata.impact}</span>
                          )}
                          {event.metadata.attendee_count > 0 && (
                            <span>{event.metadata.attendee_count} attendees</span>
                          )}
                        </div>
                      )}
                      {/* Action button */}
                      {event.related_id && event.type === 'meeting' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-blue-600"
                          onClick={() => navigate(`/meeting-intelligence/meetings/${event.related_id}`)}
                        >
                          View Meeting <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Meeting Flow Component - shows individual meeting pipeline
export const MeetingFlow = ({ meetingId }) => {
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem('auth_token');
  
  useEffect(() => {
    const fetchFlow = async () => {
      try {
        // Get project_id from meeting first
        const meetingRes = await fetch(
          `${API_URL}/api/meeting-intelligence/meetings/${meetingId}?token=${token}`
        );
        const meetingData = await meetingRes.json();
        
        if (meetingData.success && meetingData.meeting?.project_id) {
          const response = await fetch(
            `${API_URL}/api/meeting-intelligence/projects/${meetingData.meeting.project_id}/timeline/flow/${meetingId}?token=${token}`
          );
          const data = await response.json();
          if (data.success) {
            setFlow(data.flow);
          }
        }
      } catch (error) {
        console.error('Error fetching flow:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFlow();
  }, [meetingId, token]);

  if (loading) {
    return <div className="animate-pulse h-24 bg-gray-100 rounded-lg" />;
  }

  if (!flow) {
    return null;
  }

  const stages = [
    { key: 'meeting', label: 'Meeting', done: flow.meeting?.status === 'completed' },
    { key: 'transcript', label: 'Transcript', done: !!flow.transcript },
    { key: 'analysis', label: 'Analysis', done: !!flow.analysis },
    { key: 'mom', label: 'Minutes', done: !!flow.mom },
    { key: 'tasks', label: 'Tasks', done: flow.tasks?.length > 0 }
  ];

  return (
    <Card data-testid="meeting-flow">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Meeting Pipeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  stage.done 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {stage.done ? (
                    <CheckSquare className="h-5 w-5" />
                  ) : (
                    <Clock className="h-5 w-5" />
                  )}
                </div>
                <span className={`text-xs mt-1 ${stage.done ? 'text-green-600' : 'text-gray-400'}`}>
                  {stage.label}
                </span>
              </div>
              {index < stages.length - 1 && (
                <ArrowRight className={`h-4 w-4 ${
                  stages[index + 1].done ? 'text-green-400' : 'text-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 text-center">
          <span className="text-sm text-gray-500">
            {flow.completion?.percentage}% Complete
          </span>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all"
              style={{ width: `${flow.completion?.percentage || 0}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectTimeline;
